import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Cell,
} from "recharts";
import { TrendingUp, Users, Euro, Percent, MapPin, RefreshCw } from "lucide-react";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA = { fontFamily: "'Lora', Georgia, serif" };

function KpiCard({ icon: Icon, label, value, sub, color = "#C17F59", testId }) {
  return (
    <div className="card-surface flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} strokeWidth={1.5} style={{ color }} />
        <span className="text-xs uppercase tracking-widest text-[#9A8F86]" style={LORA}>{label}</span>
      </div>
      <p className="text-4xl font-semibold" style={{ ...SERIF, color: "#2B2623" }}>{value}</p>
      {sub && <p className="text-xs text-[#9A8F86] italic" style={LORA}>{sub}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#F1EBE1] border border-[#DCD4C6] px-3 py-2 shadow-sm text-sm" style={LORA}>
      <p className="font-semibold text-[#2B2623]">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#C17F59" }}>{p.name} : {p.value}{p.name === "Revenus" ? " €" : ""}</p>
      ))}
    </div>
  );
};

export default function AdminAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/analytics`);
      setData(d);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) return (
    <div className="text-center py-20">
      <div className="w-7 h-7 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-[#9A8F86] italic" style={LORA}>Calcul en cours...</p>
    </div>
  );

  if (!data) return null;
  const { kpis, revenue_by_plan, trips_by_month, top_destinations } = data;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="section-label mb-1">Performance</p>
          <h2 className="text-3xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Tableau de bord analytique</h2>
        </div>
        <button onClick={fetch} className="btn-secondary flex items-center gap-2 text-sm" data-testid="analytics-refresh-btn">
          <RefreshCw size={13} strokeWidth={1.5} /> Actualiser
        </button>
      </div>

      {/* KPIs row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard
          icon={Euro} label="Revenu total" testId="kpi-revenue"
          value={`${kpis.total_revenue.toLocaleString("fr-FR")} €`}
          sub={`${kpis.paid_trips} paiement${kpis.paid_trips > 1 ? "s" : ""}`}
        />
        <KpiCard
          icon={Percent} label="Taux de conversion" testId="kpi-conversion"
          value={`${kpis.conversion_rate} %`}
          sub={`${kpis.paid_trips} payés / ${kpis.validated_trips} validés`}
          color="#8B5E34"
        />
        <KpiCard
          icon={Users} label="Utilisateurs" testId="kpi-users"
          value={kpis.total_users}
          sub={`${kpis.total_trips} voyage${kpis.total_trips > 1 ? "s" : ""} générés`}
          color="#5C554E"
        />
        <KpiCard
          icon={TrendingUp} label="En attente" testId="kpi-pending"
          value={kpis.pending_trips}
          sub="voyage(s) à valider"
          color="#B45309"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by plan */}
        <div className="card-surface">
          <h3 className="text-xl mb-1" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Revenus par offre</h3>
          <p className="text-xs text-[#9A8F86] italic mb-5" style={LORA}>Montants encaissés par formule</p>
          {revenue_by_plan.every(r => r.total === 0) ? (
            <p className="text-sm text-[#9A8F86] italic text-center py-8" style={LORA}>Aucun paiement enregistré</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenue_by_plan} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCD4C6" vertical={false} />
                <XAxis dataKey="plan" tick={{ fontFamily: "Georgia, serif", fontSize: 12, fill: "#5C554E" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontFamily: "Georgia, serif", fontSize: 11, fill: "#9A8F86" }} axisLine={false} tickLine={false} tickFormatter={v => `${v} €`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F9F6F0" }} />
                <Bar dataKey="total" name="Revenus" radius={[2, 2, 0, 0]}>
                  {revenue_by_plan.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {/* Plan legend */}
          <div className="flex gap-4 mt-4 flex-wrap">
            {revenue_by_plan.map(r => (
              <div key={r.plan} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ background: r.color }} />
                <span className="text-xs text-[#5C554E]" style={LORA}>{r.plan} — {r.count} vente{r.count > 1 ? "s" : ""} · {r.total} €</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trips by month */}
        <div className="card-surface">
          <h3 className="text-xl mb-1" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Voyages générés</h3>
          <p className="text-xs text-[#9A8F86] italic mb-5" style={LORA}>6 derniers mois</p>
          {trips_by_month.length === 0 ? (
            <p className="text-sm text-[#9A8F86] italic text-center py-8" style={LORA}>Pas assez de données</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trips_by_month} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCD4C6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontFamily: "Georgia, serif", fontSize: 12, fill: "#5C554E" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontFamily: "Georgia, serif", fontSize: 11, fill: "#9A8F86" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone" dataKey="count" name="Voyages"
                  stroke="#C17F59" strokeWidth={2} dot={{ fill: "#C17F59", r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: "#8B5E34" }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top destinations */}
      <div className="card-surface">
        <div className="flex items-center gap-2 mb-5">
          <MapPin size={15} strokeWidth={1.5} className="text-[#C17F59]" />
          <h3 className="text-xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Top destinations</h3>
        </div>
        {top_destinations.length === 0 ? (
          <p className="text-sm text-[#9A8F86] italic" style={LORA}>Aucune destination enregistrée</p>
        ) : (
          <div className="space-y-2" data-testid="top-destinations">
            {top_destinations.map((d, i) => {
              const max = top_destinations[0]?.count || 1;
              const pct = Math.round((d.count / max) * 100);
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[#9A8F86] w-4 text-right" style={LORA}>{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#2B2623]" style={LORA}>{d.destination}</span>
                      <span className="text-xs text-[#9A8F86]" style={LORA}>{d.count} voyage{d.count > 1 ? "s" : ""}</span>
                    </div>
                    <div className="h-1.5 bg-[#EDE8E0] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, background: i === 0 ? "#C17F59" : "#DCD4C6" }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
